<template>
  <div class="modal fade" :id="modalId" tabindex="-1" role="dialog" aria-hidden="true">
    <div class="modal-dialog modal-lg" role="document">
      <div class="modal-content">

        <div class="modal-header bg-primary text-white">
          <h5 class="modal-title">{{ title }}</h5>
          <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close" />
        </div>

        <div class="modal-body">
          <!-- SHIPPING / COUPON / DISCOUNT -->
          <div v-if="field !== 'tax'">
            <div class="form-group">
              <label class="form-label">{{ label }} <span class="text-danger">*</span></label>
              <input
                  type="number"
                  min="0"
                  step="0.01"
                  class="form-control"
                  v-model.number="amount"
                  placeholder="0.00"
              />
            </div>
          </div>

          <!-- TAX -->
          <div v-else>
            <div class="row g-3">
              <div class="col-md-6">
                <label class="form-label">Order Tax <span class="text-danger">*</span></label>
                <select class="form-control" v-model="taxMode">
                  <option value="NONE">No Tax</option>
                  <option value="PERCENT">Tax (%)</option>
                  <option value="AMOUNT">Tax (Amount)</option>
                </select>
              </div>

              <div class="col-md-6" v-if="taxMode !== 'NONE'">
                <label class="form-label">
                  {{ taxMode === 'PERCENT' ? 'Tax (%)' : 'Tax (Amount)' }} <span class="text-danger">*</span>
                </label>
                <input
                    type="number"
                    :min="0"
                    step="0.01"
                    class="form-control"
                    v-model.number="taxValue"
                    :placeholder="taxMode === 'PERCENT' ? 'Ex: 18' : 'Ex: 25.00'"
                />
              </div>
            </div>

            <div class="alert alert-info mt-3" v-if="taxMode !== 'NONE'">
              Aperçu : Taxe calculée = <strong>${{ taxPreview.toFixed(2) }}</strong>
              <small class="text-muted"> (sur sous‑total ${{ subtotal.toFixed(2) }})</small>
            </div>
          </div>
        </div>

        <div class="modal-footer">
          <button class="btn btn-secondary" data-bs-dismiss="modal">Annuler</button>
          <button class="btn btn-primary" @click="onApply">Valider</button>
        </div>

      </div>
    </div>
  </div>
</template>

<script>
  /**
   * API du composant
   * ----------------
   * Props:
   *  - modalId    : String (optionnel, par défaut 'modal-payment-edit')  // utile si plusieurs modales sur la page
   *  - subtotal   : Number (obligatoire)                                 // sert au calcul d’aperçu de la taxe
   *
   * Méthodes publiques (via ref):
   *  - open(field, preset?)
   *      field: 'shipping' | 'tax' | 'coupon' | 'discount'
   *      preset (optionnel):
   *        - pour shipping/coupon/discount: { amount: Number }
   *        - pour tax: { taxMode: 'NONE'|'PERCENT'|'AMOUNT', taxValue: Number }
   *
   * Events:
   *  - save(payload)
   *      payload:
   *        { field: 'shipping'|'tax'|'coupon'|'discount',
   *          amount?: Number,             // pour shipping/coupon/discount
   *          taxMode?: String, taxValue?: Number }  // pour tax
   */
  export default {
    name: 'PaymentEditModal',
    props: {
      modalId: { type: String, default: 'modal-payment-edit' },
      subtotal: { type: Number, required: true }
    },
    data() {
      return {
        bsModal: null,
        field: null,         // 'shipping' | 'tax' | 'coupon' | 'discount'
        amount: 0,           // shipping/coupon/discount
        taxMode: 'NONE',     // NONE | PERCENT | AMOUNT
        taxValue: 0          // % ou montant
      };
    },
    computed: {
      title() {
        return {
          shipping: 'Shipping Cost',
          tax: 'Order Tax',
          coupon: 'Coupon',
          discount: 'Discount'
        }[this.field] || 'Edit';
      },
      label() {
        return {
          shipping: 'Shipping Cost',
          coupon: 'Coupon Amount',
          discount: 'Discount Amount'
        }[this.field] || 'Value';
      },
      taxPreview() {
        if (this.taxMode === 'PERCENT') return (this.subtotal * (this.taxValue || 0)) / 100;
        if (this.taxMode === 'AMOUNT')  return this.taxValue || 0;
        return 0;
      }
    },
    methods: {
      open(field, preset = {}) {
        this.field = field;

        if (field === 'tax') {
          this.taxMode = preset.taxMode ?? 'NONE';
          this.taxValue = preset.taxValue ?? 0;
        } else {
          this.amount = preset.amount ?? 0;
        }

        this.$nextTick(() => {
          if (!this.bsModal) {
            const el = document.getElementById(this.modalId);
            // eslint-disable-next-line no-undef
            this.bsModal = new bootstrap.Modal(el, { backdrop: 'static' });
          }
          this.bsModal.show();
        });
      },
      onApply() {
        // petites validations
        const nonNeg = v => (v ?? 0) >= 0;
        if (this.field === 'tax') {
          if (this.taxMode !== 'NONE' && !nonNeg(this.taxValue)) return;
          this.$emit('save', { field: 'tax', taxMode: this.taxMode, taxValue: this.taxValue });
        } else {
          if (!nonNeg(this.amount)) return;
          this.$emit('save', { field: this.field, amount: this.amount });
        }
        this.bsModal?.hide();
      }
    }
  };
</script>

<style scoped>
/* rien de spécial, la modale hérite des styles Bootstrap */
</style>
